import { EmptyNavPageContainer } from '../components/layout/EmptyNavPageContainer/EmptyNavPageContainer'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SignupPageContainer } from '../containers/authentication/SignupPageContainer/SignupPageContainer'
import { TrialsEnabledFallbackDataProvider } from '@dtx-company/app/shared/common/src/hooks/useTrialsEnabledFallbackData/TrialsEnabledFallbackDataProvider'
import { buildServerSideProps } from '@dtx-company/app/shared/common/src/utils/buildServerSideProps'
import { deserializePlanTypeQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planType/deserializePlanTypeQueryParam'
import {
  getAccessTokenFromCookies,
  getImpersonationTokenFromCookies,
  removeF2RedirectCookie
} from '../utils/auth'
import { getInviteState } from '../utils/invite'
import { getTrialsEnabledFallbackData } from '@dtx-company/app/shared/common/src/hooks/useTrialsEnabledFallbackData/useTrialsEnabledFallbackData'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useEffect } from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/cohorts'
import type { GetServerSideProps, NextPage } from 'next'
import type { SignupProps } from '../containers/authentication/SignupPageContainer/SignupPageContainer.types'
import type { UnwrapPromise } from 'next/dist/lib/coalesced-function'

export const getServerSideProps: GetServerSideProps = buildServerSideProps(async context => {
  // Determine if we are coming from flowpage or not so we can render the
  // correct text on the server-side (avoiding a hydration issue)
  const fromFlowpage = context.query.flowpage === 'true'
  const ctxPlanType = context.query.planType
  const planTypeQueryParams = ctxPlanType
    ? deserializePlanTypeQueryParam(ctxPlanType.toString())
    : ''
  const inviteState = getInviteState(context)

  const trialsEnabledFallbackData = await getTrialsEnabledFallbackData()

  removeF2RedirectCookie(context)

  const accessToken = getAccessTokenFromCookies(context.req.cookies)
  const impersonationAccessToken = getImpersonationTokenFromCookies(context.req.cookies)
  const token = impersonationAccessToken ?? accessToken

  const isLoggedIn = !!token

  if (isLoggedIn) {
    return {
      redirect: {
        permanent: false,
        destination: Routes.HOME
      },
      props: { fromFlowpage, trialsEnabledFallbackData, inviteState, planTypeQueryParams }
    }
  }

  return {
    props: {
      fromFlowpage,
      trialsEnabledFallbackData,
      planTypeQueryParams,
      inviteState
    }
  }
})

const SignupPage: NextPage<
  {
    fromFlowpage: boolean
    trialsEnabledFallbackData: UnwrapPromise<ReturnType<typeof getTrialsEnabledFallbackData>>
  } & Pick<SignupProps, 'inviteState' | 'planTypeQueryParams'>
> = ({ fromFlowpage, trialsEnabledFallbackData, inviteState, planTypeQueryParams }) => {
  const { isAuthChecked } = useAuthState()
  const { name: cohortName } = useCohort()

  useEffect(() => {
    if (isAuthChecked) {
      events.Revenue_SignupPage_Viewed_Page(cohortName)
    }
  }, [cohortName, isAuthChecked])

  return (
    <TrialsEnabledFallbackDataProvider data={trialsEnabledFallbackData}>
      <EmptyNavPageContainer page={Routes.SIGN_UP}>
        <SignupPageContainer
          fromFlowpage={fromFlowpage}
          inviteState={inviteState}
          planTypeQueryParams={planTypeQueryParams}
        />
      </EmptyNavPageContainer>
    </TrialsEnabledFallbackDataProvider>
  )
}

export default SignupPage
